import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { ApiVisa, CustomerService, EntityContainerModel, Pagination } from 'blg-akaun-ts-lib';
import { ReplaySubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AppConfig } from '../../shared-utilities/visa';

@Component({
  selector: 'blg-select-customer-drop-down',
  templateUrl: './select-customer-drop-down.component.html',
  styleUrls: ['./select-customer-drop-down.component.scss']
})
export class SelectCustomerDropDownComponent implements OnInit, OnDestroy {

  @Input() customer: FormControl;
  apiVisa = AppConfig.apiVisa;
  @Input() required: boolean;

  @Output() customerChange = new EventEmitter<FormControl>();
  @Output() customerSelected = new EventEmitter<EntityContainerModel>();

  serverSideFiltering = new FormControl();
  serverSideSearching = false;
  // replay subject replays or emits old values to new subscibers- in this case, replay the last value from the new subscriber
  filteredOptions$: ReplaySubject<EntityContainerModel[]> = new ReplaySubject<EntityContainerModel[]>(1);
  customerList: EntityContainerModel[] = [];
  selectedCustomer: EntityContainerModel;

  protected _onDestroy = new Subject<void>();

  constructor(
    private customerService: CustomerService
  ) { }

  ngOnInit(): void {
    if (this.customer.value) {
      this.customerService.getByGuid(this.customer.value, this.apiVisa).pipe(
        takeUntil(this._onDestroy)
      ).subscribe( resolve => {
        this.customerList = [resolve.data];
        this.filteredOptions$.next([resolve.data]);
        this.onSelect(resolve.data.bl_fi_mst_entity_hdr.guid.toString())
      });
    }

    this.serverSideFiltering.valueChanges.pipe(
      filter(searchText => searchText.length > 0),
      tap(() => this.serverSideSearching = true),
      // emits a value from source observable with 300ms delay
      debounceTime(300),
      // emits all items that are distinct from the previous item
      distinctUntilChanged(),
      switchMap(searchText => this.searchCustomer(searchText))
    ).subscribe( resolve => {
      const resolved = resolve.filter(x => x);
      this.serverSideSearching = false;
      if (this.selectedCustomer) {
        this.filteredOptions$.next([...resolved, this.selectedCustomer]);
        this.customerList = [...resolved, this.selectedCustomer];
      } else {
        this.filteredOptions$.next(resolved);
        this.customerList = resolved;
      }
    }, error => {
      this.serverSideSearching = false;
      console.error(error);
    });
  }

  /**
   * Server-side customer typeahead.
   *
   * Replaces the raw SELECT that went through SubQueryService:
   *
   *   FROM bl_fi_mst_entity_ext ext LEFT JOIN bl_fi_mst_entity_hdr hdr
   *   WHERE hdr.status = 'ACTIVE'
   *     AND (ext.param_code IN ('CUSTOMER_CODE','EMP_CUSTOMER_CODE')
   *          OR (ext.param_code = 'AKN_ETY_CTG' AND ext.value_string = 'CUSTOMER'))
   *     AND ((ext.param_code = 'CUSTOMER_CODE' AND ext.value_string ILIKE '%x%')
   *          OR hdr.name ILIKE '%x%')
   *   ORDER BY hdr.name
   *
   * CustomerService points at entity/customers, and EntityController sets
   * is_customer = true from that path segment, so the "is a customer" scoping the
   * ext join expressed is applied by the endpoint. EntityUow's
   * whereClauseCriteriaForSnapshot (the method behind GET entity/customers/query)
   * then applies `search_word` through EntityUowHelper.generateSearchWordCondition
   * -- with is_customer set that ILIKEs hdr.customer_code alongside name, id_no,
   * nickname, descr, phone, email, ... -- and `status` through
   * EntityUowHelper.whereClauseHdr.
   *
   * Behaviour notes:
   * - The keyword now also matches phone/email/id_no/etc., so the typeahead is
   *   broader than the old customer-code/name pair.
   * - Entities recognised as a customer only through the legacy extension rows (a
   *   `CUSTOMER_CODE` / `EMP_CUSTOMER_CODE` row, or `AKN_ETY_CTG = 'CUSTOMER'`)
   *   rather than through hdr.is_customer are kept by the `legacy_entity_category`
   *   criterion, which makes EntityUowHelper.whereClauseHdr OR the same extension
   *   test onto the is_customer condition. Without it those entities would drop out
   *   of the dropdown, because the endpoint derives is_customer from the path.
   *   It costs a correlated subquery per request: once
   *   `entity-category-flag-check.sql` reports a zero gap for every tenant, the flag
   *   is backfilled and this criterion can be dropped.
   * - One request instead of one subquery plus one getByCriteria per hit; the
   *   `catchError` that swallowed per-guid CORS failures is no longer needed
   *   because there is only one request, whose error path the caller handles.
   */
  searchCustomer(searchText: string) {
    return this.customerService.getByCriteria(
      new Pagination(0, 100, [
        { columnName: 'status', operator: '=', value: 'ACTIVE' },
        { columnName: 'legacy_entity_category', operator: '=', value: 'CUSTOMER' },
        { columnName: 'search_word', operator: '=', value: (searchText || '').trim() }
      ]),
      this.apiVisa
    ).pipe(
      map(res => res.data)
    );
  }

  onSelect(entityGuid: string) {
    if (entityGuid) {
      this.customer.patchValue(entityGuid);
      this.selectedCustomer = this.customerList.find(customer => customer.bl_fi_mst_entity_hdr.guid === entityGuid);
      this.customerSelected.emit(this.selectedCustomer);
    }
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

}
